<script setup>
import { computed } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  industryId: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const UI_LABELS = {
  title: 'Egna frågor och svar',
  intro:
    'Lägg in vanliga kundfrågor och skriv ett kort svar som Chatlotta kan föreslå. Alla förslag granskas av en person före utskick.',
  add: 'Lägg till fler frågor',
  addExample: 'Lägg till exempel',
  examplesTitle: 'Exempel för restaurang',
  remove: 'Ta bort',
  customerQuestion: 'Kundens fråga',
  customerQuestionHelp: 'Skriv frågan så som en kund brukar formulera den.',
  ownerAnswer: 'Ägarens/interna svar',
  ownerAnswerHelp:
    'Rå eller intern företagsinformation. Den visas inte som kundsvar när ett vänligt kundsvar finns.',
  friendlyAnswer: 'Vänligt kundsvar',
  friendlyAnswerHelp:
    'Det korta, professionella svar som Chatlotta ska föreslå till kunden. Detta svar prioriteras alltid.',
  category: 'Kategori',
  categoryHelp: 'Gruppera frågan, till exempel meny, bokning eller öppettider.',
  botAllowed: 'Använd i Chatlotta-förslag',
  botAllowedHelp: 'Gör svaret tillgängligt som ett manuellt granskat förslag.',
  humanReview: 'Kräver mänsklig granskning',
  humanReviewHelp:
    'Behåll detta valt när en medarbetare ska kontrollera svaret.',
  missingQuestion: 'Lägg till kundens fråga.',
  missingAnswer: 'Lägg till ägarens/interna svar.',
  missingFriendlyAnswer:
    'Lägg till ett vänligt kundsvar som Chatlotta kan föreslå.',
  vagueAnswer:
    'Svaret är mycket kort. Lägg gärna till tydlig och användbar information.',
};

const RESTAURANT_EXAMPLES = [
  'Har ni vegetariska alternativ?',
  'Har ni allergivänliga alternativ?',
  'Behöver jag boka bord?',
  'Har ni takeaway?',
  'Vilka öppettider har ni?',
  'Kan man boka för större sällskap?',
];

const suggestedExamples = computed(() =>
  props.industryId === 'restaurant' ? RESTAURANT_EXAMPLES : []
);

const blankQuestion = () => ({
  customer_question: '',
  owner_answer: '',
  friendly_answer: '',
  category: '',
  bot_allowed: true,
  requires_human_review: true,
});

const updateQuestions = questions => {
  emit('update:modelValue', questions);
};

const addQuestion = () => {
  updateQuestions([...props.modelValue, blankQuestion()]);
};

const addExample = customerQuestion => {
  updateQuestions([
    ...props.modelValue,
    { ...blankQuestion(), customer_question: customerQuestion },
  ]);
};

const warningsFor = question => {
  const warnings = [];
  if (!question.customer_question?.trim())
    warnings.push(UI_LABELS.missingQuestion);
  if (!question.owner_answer?.trim()) warnings.push(UI_LABELS.missingAnswer);
  if (!question.friendly_answer?.trim()) {
    warnings.push(UI_LABELS.missingFriendlyAnswer);
  } else if (question.friendly_answer.trim().length < 20) {
    warnings.push(UI_LABELS.vagueAnswer);
  }
  return warnings;
};

const removeQuestion = index => {
  updateQuestions(
    props.modelValue.filter((_, itemIndex) => itemIndex !== index)
  );
};

const updateQuestion = (index, key, value) => {
  updateQuestions(
    props.modelValue.map((question, itemIndex) =>
      itemIndex === index ? { ...question, [key]: value } : question
    )
  );
};
</script>

<template>
  <div class="border-t border-n-weak px-3 py-3">
    <div class="mb-3 flex items-center justify-between gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ UI_LABELS.title }}
      </span>
      <NextButton
        type="button"
        :label="UI_LABELS.add"
        icon="i-lucide-plus"
        slate
        xs
        outline
        @click="addQuestion"
      />
    </div>

    <p class="mb-3 text-sm text-n-slate-10">{{ UI_LABELS.intro }}</p>

    <div
      v-if="suggestedExamples.length"
      class="mb-3 grid gap-2 rounded-md bg-n-alpha-2 p-3"
      data-test-id="chatlotta-faq-examples"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ UI_LABELS.examplesTitle }}
      </span>
      <div
        v-for="example in suggestedExamples"
        :key="example"
        class="flex items-center justify-between gap-2"
      >
        <span class="text-sm text-n-slate-11">{{ example }}</span>
        <NextButton
          type="button"
          :label="UI_LABELS.addExample"
          slate
          xs
          outline
          @click="addExample(example)"
        />
      </div>
    </div>

    <div v-if="modelValue.length" class="space-y-3">
      <section
        v-for="(question, index) in modelValue"
        :key="index"
        class="space-y-3 rounded-md border border-n-weak p-3"
      >
        <div class="flex justify-end">
          <NextButton
            type="button"
            :label="UI_LABELS.remove"
            icon="i-lucide-trash-2"
            ruby
            xs
            ghost
            @click="removeQuestion(index)"
          />
        </div>

        <Input
          :model-value="question.customer_question"
          :label="UI_LABELS.customerQuestion"
          placeholder="Har ni vegetariska alternativ?"
          size="sm"
          @update:model-value="
            value => updateQuestion(index, 'customer_question', value)
          "
        />
        <p class="-mt-2 text-xs text-n-slate-10">
          {{ UI_LABELS.customerQuestionHelp }}
        </p>
        <Textarea
          :model-value="question.owner_answer"
          :label="UI_LABELS.ownerAnswer"
          placeholder="Ja, vi har alltid minst två vegetariska rätter och kan ofta anpassa vissa rätter."
          :max-length="1000"
          resize
          @update:model-value="
            value => updateQuestion(index, 'owner_answer', value)
          "
        />
        <p class="-mt-2 text-xs text-n-slate-10">
          {{ UI_LABELS.ownerAnswerHelp }}
        </p>
        <Textarea
          :model-value="question.friendly_answer"
          :label="UI_LABELS.friendlyAnswer"
          placeholder="Hej! Ja, vi har vegetariska alternativ. Fråga oss gärna om dagens vegetariska rätter eller meddela om du har särskilda önskemål."
          :max-length="1000"
          resize
          @update:model-value="
            value => updateQuestion(index, 'friendly_answer', value)
          "
        />
        <p class="-mt-2 text-xs text-n-slate-10">
          {{ UI_LABELS.friendlyAnswerHelp }}
        </p>
        <Input
          :model-value="question.category"
          :label="UI_LABELS.category"
          size="sm"
          @update:model-value="
            value => updateQuestion(index, 'category', value)
          "
        />
        <p class="-mt-2 text-xs text-n-slate-10">
          {{ UI_LABELS.categoryHelp }}
        </p>

        <div class="grid gap-2 sm:grid-cols-2">
          <label class="flex items-center gap-2 text-sm text-n-slate-11">
            <input
              type="checkbox"
              class="size-4"
              :checked="question.bot_allowed"
              @change="
                event =>
                  updateQuestion(index, 'bot_allowed', event.target.checked)
              "
            />
            <span>{{ UI_LABELS.botAllowed }}</span>
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-11">
            <input
              type="checkbox"
              class="size-4"
              :checked="question.requires_human_review"
              @change="
                event =>
                  updateQuestion(
                    index,
                    'requires_human_review',
                    event.target.checked
                  )
              "
            />
            <span>{{ UI_LABELS.humanReview }}</span>
          </label>
        </div>
        <p class="text-xs text-n-slate-10">
          {{ UI_LABELS.botAllowedHelp }} {{ UI_LABELS.humanReviewHelp }}
        </p>

        <ul
          v-if="warningsFor(question).length"
          class="mb-0 list-disc space-y-1 pl-5 text-xs text-n-amber-11"
          data-test-id="chatlotta-faq-warnings"
        >
          <li v-for="warning in warningsFor(question)" :key="warning">
            {{ warning }}
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>
